// Base
import React from "react";
import { Controller } from "react-hook-form";

// Store
import { useTemplateStore } from "@/zustand/templateStore";

// Components
import TextField from "@/components/Inputs/TextField";
import PhoneField from "@/components/Inputs/PhoneField";
import CountryField from "@/components/Inputs/CountryField";
import RegionField from "@/components/Inputs/RegionField";

// Types
import type { Ref } from "react";
import type {
  Control,
  FieldValues,
  FieldErrors,
  UseFormRegister,
} from "react-hook-form";

interface UserInformationProps {
  control: Control<FieldValues>;
  errors: FieldErrors;
  register: UseFormRegister<FieldValues>;
  form: Record<string, string | undefined> | null;
  itiRef?: Ref<HTMLInputElement>;
  country?: string;
  country_code?: string;
  setCountry: (country: string) => void;
}

const UserInformation: React.FC<UserInformationProps> = ({
  control,
  errors,
  register,
  form,
  itiRef,
  country,
  country_code,
  setCountry,
}) => {
  const { settings } = useTemplateStore();

  return (
    <div>
      <h1
        className={`mb-4 text-xl font-bold ${
          settings.enable_high_contrast ? "text-cc-dark-gray" : "text-cc-gray"
        }`}
      >
        {form?.tr_step_2_title}
      </h1>
      <div className="flex flex-col gap-3">
        <Controller
          control={control}
          name="email"
          render={({ field: { onChange, value } }) => (
            <TextField
              contrast={settings.enable_high_contrast}
              type="email"
              placeholder={form?.tr_email_address_label || "Email Address"}
              name="email"
              error={
                errors.email?.message ? String(errors.email.message) : undefined
              }
              register={register}
              value={value}
              handleChange={onChange}
            />
          )}
        />

        <Controller
          control={control}
          name="first_name"
          render={({ field: { onChange, value } }) => (
            <TextField
              contrast={settings.enable_high_contrast}
              type="text"
              placeholder={form?.tr_first_name_label || "First Name"}
              name="first_name"
              autoComplete="cc-given-name"
              value={value}
              handleChange={onChange}
              register={register}
              error={
                errors.first_name
                  ? String(errors.first_name.message)
                  : undefined
              }
            />
          )}
        />

        <Controller
          control={control}
          name="last_name"
          render={({ field: { onChange, value } }) => (
            <TextField
              contrast={settings.enable_high_contrast}
              type="text"
              placeholder={form?.tr_last_name_label || "Last Name"}
              name="last_name"
              autoComplete="cc-family-name"
              error={
                errors.last_name ? String(errors.last_name.message) : undefined
              }
              register={register}
              value={value}
              handleChange={onChange}
            />
          )}
        />

        <Controller
          control={control}
          name="phone"
          render={({ field: { onChange, value } }) => (
            <PhoneField
              contrast={settings.enable_high_contrast}
              ref={itiRef}
              placeholder={form?.tr_phone_number_label || "Phone Number"}
              value={value}
              error={errors.phone ? String(errors.phone.message) : undefined}
              country={country}
              handleChange={onChange}
            />
          )}
        />

        <Controller
          control={control}
          name="address"
          render={({ field: { onChange, value } }) => (
            <TextField
              contrast={settings.enable_high_contrast}
              type="text"
              placeholder={form?.tr_address_label || "Address"}
              error={
                errors.address ? String(errors.address.message) : undefined
              }
              name="address"
              autoComplete="street-address"
              register={register}
              value={value}
              handleChange={onChange}
            />
          )}
        />

        <Controller
          control={control}
          name="city"
          render={({ field: { onChange, value } }) => (
            <TextField
              contrast={settings.enable_high_contrast}
              type="text"
              placeholder={form?.tr_city_label || "City"}
              name="city"
              register={register}
              autoComplete="address-level2"
              error={errors.city ? String(errors.city.message) : undefined}
              value={value}
              handleChange={onChange}
            />
          )}
        />

        <Controller
          control={control}
          name="country"
          render={({ field: { onChange } }) => (
            <CountryField
              contrast={settings.enable_high_contrast}
              placeholder={form?.tr_country_label || "Country"}
              name="country"
              error={
                errors.country ? String(errors.country.message) : undefined
              }
              value={country}
              handleChange={(val: string) => {
                onChange(val);
                setCountry(val);
              }}
            />
          )}
        />

        <div className="flex flex-col gap-3 md:flex-row">
          <Controller
            control={control}
            name="state"
            render={({ field: { onChange, value } }) => (
              <RegionField
                contrast={settings.enable_high_contrast}
                name="region"
                label={form?.tr_state_province_label || "State / Province"}
                error={errors.state ? String(errors.state.message) : undefined}
                country={country || country_code?.toUpperCase()}
                handleChange={onChange}
                value={value}
              />
            )}
          />

          <Controller
            control={control}
            name="zip"
            render={({ field: { onChange, value } }) => (
              <TextField
                contrast={settings.enable_high_contrast}
                type="text"
                placeholder={
                  form?.tr_zip_postal_code_label || "ZIP / Postal Code"
                }
                error={errors.zip ? String(errors.zip.message) : undefined}
                name="zip"
                register={register}
                autoComplete="postal-code"
                value={value}
                handleChange={onChange}
              />
            )}
          />
        </div>
      </div>
    </div>
  );
};

export default UserInformation;
